import React from 'react';
import {
  Phone,
  ShieldCheck,
  ArrowRight,
} from 'lucide-react';
import { GoodBullet, WarnBullet } from './Bullets.jsx';

/**
 * Step 1 — Local vs Toll-Free selection.
 * @param {{ numberType: 'local'|'tollfree', setNumberType: (t: string) => void, onNext: () => void, onSelectTollFree: () => void }} props
 */
export default function StepType({ numberType, setNumberType, onNext, onSelectTollFree }) {
  const localActive = numberType === 'local';
  return (
    <div className="space-y-4">
      <div>
        <div className="font-display font-bold text-lg">Choose a number type</div>
        <div className="text-xs text-white/55">US-only numbers. Pick the kind that fits your outreach.</div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {/* Local number — the recommended, fully trusted outbound option. */}
        <button
          type="button"
          onClick={() => setNumberType('local')}
          aria-pressed={localActive}
          className={`text-left p-4 rounded-md border transition flex flex-col ${
            localActive
              ? 'border-civix-purple/60 bg-civix-purple/10 ring-2 ring-civix-purple/40'
              : 'border-civix-border/50 hover:border-civix-purple/40'
          }`}
        >
          <div className="flex items-center gap-2 font-display font-bold">
            <Phone className="w-4 h-4 text-civix-purple" /> Local number ($1 / 30 days)
          </div>
          <ul className="mt-3 space-y-1.5">
            <GoodBullet>Automatic STIR/SHAKEN A-attestation</GoodBullet>
            <GoodBullet>Automatic spam-protection registration (Numeracle · FCR · First Orion · TNS)</GoodBullet>
            <GoodBullet>SMS + Voice enabled</GoodBullet>
            <GoodBullet>Area-code matching for local presence dialing</GoodBullet>
          </ul>
          <span className="mt-3 inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-md border border-emerald-500/40 bg-emerald-500/15 text-emerald-300 text-[11px] font-medium">
            RECOMMENDED for outbound dialing/texting
          </span>
        </button>

        {/* Toll-Free number — voice-only inbound main line; separate flow. */}
        <button
          type="button"
          onClick={onSelectTollFree}
          aria-pressed={false}
          className="text-left p-4 rounded-md border transition flex flex-col border-civix-border/50 hover:border-amber-400/40"
        >
          <div className="flex items-center gap-2 font-display font-bold">
            <ShieldCheck className="w-4 h-4 text-amber-300" /> Toll-Free number ($10 / 30 days)
          </div>
          <ul className="mt-3 space-y-1.5">
            <WarnBullet>Voice-only — SMS blocked in CIVIX</WarnBullet>
            <li className="flex items-start gap-2 text-xs text-white/60">
              <span className="w-3.5 shrink-0" />Requires Toll-Free Verification (2-6 week approval)
            </li>
            <li className="flex items-start gap-2 text-xs text-white/60">
              <span className="w-3.5 shrink-0" />Best for a public main-line number for inbound calls
            </li>
            <li className="flex items-start gap-2 text-xs text-white/60">
              <span className="w-3.5 shrink-0" />Cannot do STIR/SHAKEN attestation (Toll-Free tech limit)
            </li>
          </ul>
          <span className="mt-3 inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-md border border-amber-500/40 bg-amber-500/15 text-amber-200 text-[11px] font-medium">
            For inbound main line only
          </span>
        </button>
      </div>
      <div className="flex justify-end">
        <button onClick={onNext} className="btn-primary inline-flex items-center gap-2">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
